// TAT dashboard filter vocabulary.
//
// Status chips used to be a client-side constant here (`TAT_STATUS_GROUPS`,
// `statusCodesForGroup`) because the backend's server-side `statusGroup`
// expansion was explicitly deferred when this was first built. It shipped in
// Phase 13 (pillup-api/docs/tat-dashboard/execution-plan.md) — GET
// /api/ops/tat/filters now returns the real `statusGroups` (and
// `sortOptions`) itself, and by the time Phase 13 landed this file's
// hardcoded groupings had already drifted from the server's real ones (e.g.
// this file put ORDER_PENDING/ORDER_ACTIVE under "To Process"; the server's
// real QUOTATION group puts them there instead). Sort/status chips now
// render from src/v2/ops/hooks/useTatFilters.js's live data — see index.jsx —
// so that class of drift can't happen again. Only the sort-by vocabulary
// stays here, because /filters returns bare ids for it with no labels.

/**
 * Sort options, single-select.
 *
 * The `id`s are the exact keys the backend's SORT_SQL / `/ops/tat/filters`
 * `sortOptions` accept (pillup-api controller/ops/tat.js) — not a parallel
 * list that has to be kept in step; only the human labels live here, since
 * /filters returns bare ids.
 *
 * `end_to_end`/`end_to_end_asc` were disabled here for a while (`broken`,
 * removed 2026-08-31) on a diagnosis that turned out to be a snapshot, not a
 * bug: at the time, sort=end_to_end and sort=end_to_end_asc returned
 * identical row order because ZERO orders had both a linked lead
 * (`leadGenesis.createdAt`) and a real delivered timestamp (`uo.deliveredAt`)
 * — controller/ops/tat.js's `end_to_end_minutes` sort key is
 * `uo."deliveredAt" - "leadGenesis"."createdAt"`, the exact same two columns
 * the row's own `stages.leadCreatedAt`/`stages.deliveredAt` (and so
 * `tat.endToEnd`) already read — with nothing to compare, NULLS LAST parks
 * every row in the same order regardless of direction. That's data scarcity,
 * not a wrong-column bug — the sort SQL was correct the whole time.
 *
 * Re-verified live 2026-08-31 against order 14251, which now has a real,
 * COMPLETE `tat.endToEnd` (37 min): it sorts to position 1 under both
 * directions (correct — it's still the only order with both anchors
 * populated, so nothing to compare against yet, but the mechanism now
 * demonstrably works rather than being untestable). Two in-progress orders
 * with a non-null `tat.endToEnd` (14246/14247) correctly stay NULL in the
 * sort key, because they aren't delivered yet — you can't sort a duration
 * that hasn't finished. This will get more useful, not more broken, as more
 * orders accumulate both a lead link and a delivery.
 */
export const TAT_SORT_OPTIONS = [
    { id: "recent", label: "Most Recent" },
    { id: "end_to_end", label: "Longest End-to-End" },
    { id: "end_to_end_asc", label: "Shortest End-to-End" },
    { id: "in_progress", label: "In-Progress Aging" },
    { id: "status", label: "Current Status" },
];

export const DEFAULT_TAT_SORT = "recent";

/**
 * "All" is this page's own convention, not a server concept — /ops/tat/filters'
 * `statusGroups` never includes an "all" entry, so index.jsx prepends this chip
 * itself in front of whatever the server returns.
 */
export const DEFAULT_TAT_STATUS_GROUP = "all";

/**
 * Partner scope, single-select.
 *
 * The `id`s are the exact values `/ops/tat/orders`' `partnerType` accepts. The server
 * treats them case-insensitively; they are sent upper-case here because that is how the
 * contract documents them, and a filter that reads differently on the wire than in the
 * docs is a debugging tax for no gain.
 *
 * DIRECT first because it is the server's default — and because it has been in force,
 * invisibly, since this dashboard shipped. The page never sent `partnerType`, so it has
 * always been showing direct orders only while appearing to show everything. Putting
 * the chips on screen does not change what loads by default; it stops the default being
 * a secret.
 *
 * The server also accepts ALL, and this page deliberately does not offer it. Direct and
 * partner orders do not share a journey — a partner order has no lead and no quotation,
 * and starts at Order Created — so a mixed page would put two different stage sets in
 * one set of columns and invite averaging across them. The scope is a choice of which
 * funnel you are looking at, and it is always one of the two. A hand-written
 * `?partnerType=ALL` is not honoured either; see index.jsx.
 */
export const TAT_PARTNER_TYPES = [
    { id: "DIRECT", label: "Direct" },
    { id: "PARTNER", label: "Partner" },
];

/** Matches the server's own default for `partnerType`. */
export const DEFAULT_TAT_PARTNER_TYPE = "DIRECT";

/**
 * Which partner the orders came through, single-select.
 *
 * The `id`s are the server's `partnerSource` enum verbatim; only the casing of the
 * labels lives here, because the raw values are shouted enum constants and
 * "TATVAPRACTICE" is not how anyone writes it down.
 *
 * This replaced a picker driven by `partnerId` and `/partner/admin`. That list was the
 * partner TABLE — fourteen-odd rows including individual prescribing doctors — so
 * picking "the Visit business" meant knowing which row id stood for it. Source is the
 * grouping the business actually uses, and there are four of them.
 */
export const TAT_PARTNER_SOURCES = [
    { id: "VISIT", label: "Visit" },
    { id: "TATVAPRACTICE", label: "Tatvapractice" },
    { id: "GLP_1", label: "GLP-1" },
    { id: "NUTRAPACK", label: "Nutrapack" },
];

/**
 * A partner source is only a filter on orders that can have a partner.
 *
 * Combined with `partnerType=DIRECT` it describes the empty set, so the page hides the
 * picker rather than offering a combination that can only ever return nothing. Kept as a
 * list rather than collapsed to a comparison against "PARTNER" so that restoring a mixed
 * scope later is a one-entry change here and nothing else.
 */
export const PARTNER_TYPES_ACCEPTING_PARTNER_SOURCE = ["PARTNER"];

/**
 * A source code as a person would write it.
 *
 * `order.source` is NOT a partner-only field — alongside the four partner sources it
 * carries META, APP, INTERNAL and others on direct orders, so this cannot just be a
 * lookup in TAT_PARTNER_SOURCES. The lookup runs first because mechanical title-casing
 * mangles the ones that matter ("GLP_1" becomes "Glp 1"); everything else falls back to
 * title case, which is right for the plain words and keeps one casing down the column.
 * An unrecognised code still renders legibly rather than blank, the same bargain
 * `getStatusMeta` strikes.
 */
export function sourceLabel(code) {
    if (!code || typeof code !== "string") return null;
    const known = TAT_PARTNER_SOURCES.find((source) => source.id === code.toUpperCase());
    if (known) return known.label;
    return code
        .toLowerCase()
        .split("_")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}
